import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; 
import api from './api';

// --- HELPER: Date Formatter (DD-MM-YYYY) ---
const formatDate = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-GB'); // en-GB gives DD/MM/YYYY
};

// --- HELPER: Currency Formatter (2 Decimals) ---
const formatMoney = (amount) => {
    return (amount || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const LaborManager = () => {
    const navigate = useNavigate(); 
    
    // View State
    const [showAdmin, setShowAdmin] = useState(false);
    const [tab, setTab] = useState('issue'); 
    
    // DATA
    const [depts, setDepts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [pendingJobs, setPendingJobs] = useState([]);
    
    // FORMS
    const [issueForm, setIssueForm] = useState({ packetNo: '', empId: '', procId: '', qty: 1 });
    
    // RECEIVE MODAL STATE
    const [showRecModal, setShowRecModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [returnQty, setReturnQty] = useState(1);

    useEffect(() => {
        loadMasters();
        loadPending();
    }, []);

    const loadMasters = async () => {
        try {
            const [d, e, p] = await Promise.all([
                api.get('/labor/dept'),
                api.get('/labor/employee'),
                api.get('/labor/process')
            ]);
            setDepts(d.data);
            setEmployees(e.data);
            setProcesses(p.data);
        } catch (err) { console.error("Error loading masters"); }
    };

    const loadPending = async () => {
        try {
            const res = await api.get('/labor/pending');
            setPendingJobs(res.data);
        } catch (err) { console.error("Error loading pending jobs"); }
    };

    // --- ISSUE ACTION ---
    const handleIssue = async (e) => {
        e.preventDefault();
        try {
            await api.post('/labor/issue', {
                packetNo: issueForm.packetNo,
                employeeId: issueForm.empId,
                processId: issueForm.procId,
                quantity: issueForm.qty
            });
            // Kiosk Mode: No alert, just clear and refocus
            setIssueForm({ ...issueForm, packetNo: '', qty: 1 }); 
            loadPending();
            const input = document.getElementById('packetInput');
            if(input) input.focus();
        } catch (err) { alert("Error issuing"); }
    };

    // --- RECEIVE ACTIONS ---
    const openReceiveModal = (job) => {
        setSelectedJob(job);
        setReturnQty(job.quantity); 
        setShowRecModal(true);
    };

    const confirmReturn = async () => {
        try {
            await api.put(`/labor/return/${selectedJob._id}`, { returnQty: returnQty });
            setShowRecModal(false);
            loadPending();
        } catch (err) { alert("Error receiving"); }
    };

    // --- MASTERS COMPONENT ---
    const MastersView = () => {
        const [mForm, setMForm] = useState({ type: 'emp', name: '', rate: '', dept: '' });
        const [editingId, setEditingId] = useState(null);

        const handleEdit = (item, type) => {
            setEditingId(item._id);
            setMForm({
                type: type,
                name: item.name,
                rate: item.rate || '',
                dept: item.department?._id || item.department || ''
            });
            document.getElementById('masterForm')?.scrollIntoView({ behavior: 'smooth' });
        };

        const saveMaster = async () => {
            try {
                const payload = { name: mForm.name };
                if (mForm.type === 'emp') payload.department = mForm.dept;
                if (mForm.type === 'proc') { payload.rate = mForm.rate; payload.department = mForm.dept; }

                if (editingId) {
                    if(mForm.type === 'dept') await api.put(`/labor/dept/${editingId}`, payload);
                    if(mForm.type === 'emp') await api.put(`/labor/employee/${editingId}`, payload);
                    if(mForm.type === 'proc') await api.put(`/labor/process/${editingId}`, payload);
                    alert("Updated!");
                } else {
                    if(mForm.type === 'dept') await api.post('/labor/dept', payload);
                    if(mForm.type === 'emp') await api.post('/labor/employee', payload);
                    if(mForm.type === 'proc') await api.post('/labor/process', payload);
                    alert("Created!");
                }
                loadMasters();
                resetForm();
            } catch(err) { alert("Error saving"); }
        };

        const handleDelete = async (id, type) => {
            if(!window.confirm("Delete this?")) return;
            try { await api.delete(`/labor/master/${type}/${id}`); loadMasters(); } 
            catch(err) { alert("Error deleting"); }
        };

        const resetForm = () => {
            setEditingId(null);
            setMForm({ ...mForm, name: '', rate: '', dept: '' });
        };

        return (
            <div>
                {/* Form */}
                <div id="masterForm" className="card p-4 bg-white mb-4 shadow-sm border-primary">
                    <h5 className="fw-bold text-primary border-bottom pb-2">{editingId ? '✏️ Update' : '➕ Add New'}</h5>
                    <div className="row g-3">
                        <div className="col-md-2">
                            <label className="small fw-bold">Type</label>
                            <select className="form-select" value={mForm.type} onChange={e => { setMForm({...mForm, type: e.target.value}); resetForm(); }}>
                                <option value="emp">Employee</option>
                                <option value="proc">Process Rate</option>
                                <option value="dept">Department</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold">Name</label>
                            <input className="form-control" placeholder="Name" value={mForm.name} onChange={e => setMForm({...mForm, name: e.target.value})} />
                        </div>
                        {mForm.type === 'proc' && (
                            <div className="col-md-2">
                                <label className="small fw-bold">Rate (₹)</label>
                                <input className="form-control" type="number" placeholder="Rate" value={mForm.rate} onChange={e => setMForm({...mForm, rate: e.target.value})} />
                            </div>
                        )}
                        {mForm.type !== 'dept' && (
                            <div className="col-md-3">
                                <label className="small fw-bold">Department</label>
                                <select className="form-select" value={mForm.dept} onChange={e => setMForm({...mForm, dept: e.target.value})}>
                                    <option value="">- Select -</option>
                                    {depts.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="col-md-2 align-self-end d-flex gap-2">
                            <button className={`btn w-100 fw-bold ${editingId ? 'btn-warning' : 'btn-primary'}`} onClick={saveMaster}>{editingId ? 'UPDATE' : 'ADD'}</button>
                            {editingId && <button className="btn btn-secondary" onClick={resetForm}>X</button>}
                        </div>
                    </div>
                </div>

                {/* Lists */}
                <div className="card shadow-sm">
                    <div className="card-header bg-dark text-white fw-bold">Existing Records</div>
                    <table className="table table-hover mb-0 align-middle">
                        <thead className="table-light">
                            <tr><th>Name</th>{mForm.type !== 'dept' && <th>Department</th>}{mForm.type === 'proc' && <th>Rate</th>}<th className="text-end">Action</th></tr>
                        </thead>
                        <tbody>
                            {mForm.type === 'emp' && employees.map(e => (
                                <tr key={e._id}><td>{e.name}</td><td>{e.department?.name}</td><td className="text-end"><button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(e, 'emp')}>Edit</button><button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(e._id, 'emp')}>Del</button></td></tr>
                            ))}
                            {mForm.type === 'dept' && depts.map(d => (
                                <tr key={d._id}><td>{d.name}</td><td className="text-end"><button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(d, 'dept')}>Edit</button><button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(d._id, 'dept')}>Del</button></td></tr>
                            ))}
                            {mForm.type === 'proc' && processes.map(p => (
                                <tr key={p._id}><td>{p.name}</td><td>{p.department?.name}</td><td className="fw-bold">₹ {p.rate}</td><td className="text-end"><button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(p, 'proc')}>Edit</button><button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p._id, 'proc')}>Del</button></td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid bg-light min-vh-100 p-0">
            
            {/* KIOSK HEADER */}
            <div className="bg-dark text-white p-3 d-flex justify-content-between align-items-center shadow">
                <div className="d-flex align-items-center gap-3">
                    <h3 className="mb-0 fw-bold">🏭 FACTORY KIOSK</h3>
                    <span className="badge bg-secondary">Live Operations</span>
                </div>
                
                {/* --- NAVIGATION LINKS --- */}
                <div className="d-flex gap-2">
                    
                    {/* LINK TO GALAXY MODULE */}
                    <button 
                        className="btn btn-outline-info text-white btn-sm px-3 fw-bold"
                        onClick={() => navigate('/galaxy-job?mode=kiosk')}
                    >
                        🌌 GALAXY DEPT
                    </button>

                    <div className="vr bg-secondary mx-1"></div>

                    <button className={`btn btn-sm ${showAdmin ? 'btn-warning' : 'btn-outline-secondary text-white'}`} onClick={() => setShowAdmin(!showAdmin)}>
                        {showAdmin ? 'Close Admin' : '⚙️ Admin'}
                    </button>
                    
                    <button className="btn btn-danger btn-sm px-3 fw-bold" onClick={() => navigate('/')}>EXIT</button>
                </div>
            </div>

            {/* ADMIN PANEL */}
            {showAdmin && (
                <div className="p-3 bg-white border-bottom">
                    <div className="d-flex gap-2 mb-3">
                        <button className={`btn ${tab === 'issue' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('issue')}>Operations</button>
                        <button className={`btn ${tab === 'masters' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('masters')}>Masters Setup</button>
                        <button className={`btn ${tab === 'report' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('report')}>Wage Reports</button>
                    </div>
                    {tab === 'masters' && <MastersView />}
                    {tab === 'report' && <LaborReport />}
                </div>
            )}

            {/* MAIN OPERATIONS */}
            {(!showAdmin || tab === 'issue') && (
                <div className="row g-0 h-100">
                    {/* LEFT: ISSUE */}
                    <div className="col-md-4 bg-white border-end p-4" style={{minHeight: '90vh'}}>
                        <h4 className="fw-bold text-primary mb-4 border-bottom pb-2">1. ISSUE PACKET</h4>
                        <form onSubmit={handleIssue}>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">KAPAN / JANGAD NO</label>
                                <input id="packetInput" className="form-control form-xl fw-bold border-primary" autoFocus placeholder="Scan Here..." value={issueForm.packetNo} onChange={e => setIssueForm({...issueForm, packetNo: e.target.value})} required />
                            </div>
                            <div className="row g-2 mb-3">
                                <div className="col-8">
                                    <label className="fw-bold text-muted">WORKER</label>
                                    <select className="form-select form-xl" value={issueForm.empId} onChange={e => setIssueForm({...issueForm, empId: e.target.value})} required>
                                        <option value="">Select Worker...</option>
                                        {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-4">
                                    <label className="fw-bold text-muted">QTY</label>
                                    <input type="number" className="form-control form-xl text-center fw-bold" value={issueForm.qty} onChange={e => setIssueForm({...issueForm, qty: e.target.value})} />
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="fw-bold text-muted">PROCESS RATE</label>
                                <select className="form-select form-xl" value={issueForm.procId} onChange={e => setIssueForm({...issueForm, procId: e.target.value})} required>
                                    <option value="">Select Process...</option>
                                    {processes.map(p => <option key={p._id} value={p._id}>{p.name} (₹{p.rate})</option>)}
                                </select>
                            </div>
                            <button className="btn btn-primary btn-xl w-100 fw-bold shadow-sm">🚀 ISSUE PACKET</button>
                        </form>
                    </div>

                    {/* RIGHT: PENDING */}
                    <div className="col-md-8 bg-light p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-dark mb-0">2. ON FLOOR (PENDING RETURN)</h4>
                            <span className="badge bg-warning text-dark fs-6">{pendingJobs.length} Pending</span>
                        </div>
                        <div className="table-responsive bg-white shadow-sm rounded">
                            <table className="table table-hover mb-0 align-middle" style={{fontSize: '1.1rem'}}>
                                <thead className="bg-secondary text-white">
                                    <tr><th className="py-3 ps-3">Kapan / Pkt</th><th className="py-3">Qty</th><th className="py-3">Worker</th><th className="py-3">Process</th><th className="py-3 text-center">Action</th></tr>
                                </thead>
                                <tbody>
                                    {pendingJobs.length === 0 ? <tr><td colSpan="5" className="text-center p-5 text-muted display-6">All clear!</td></tr> : 
                                        pendingJobs.map(job => (
                                            <tr key={job._id} style={{cursor:'pointer'}} onClick={() => openReceiveModal(job)}>
                                                <td className="fw-bold ps-3 text-primary">{job.packetNo}</td>
                                                <td className="fw-bold">{job.quantity}</td>
                                                <td>{job.employee?.name}</td>
                                                <td>{job.process?.name}</td>
                                                <td className="text-center"><button className="btn btn-success btn-lg px-4 fw-bold">RECEIVE</button></td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* RECEIVE MODAL */}
            {showRecModal && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.8)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg p-2">
                            <div className="modal-header bg-success text-white">
                                <h4 className="modal-title fw-bold">✅ CONFIRM COMPLETION</h4>
                                <button className="btn-close btn-close-white" onClick={() => setShowRecModal(false)}></button>
                            </div>
                            <div className="modal-body text-center p-4">
                                <h5 className="text-muted mb-1">Worker: <span className="text-dark fw-bold">{selectedJob?.employee?.name}</span></h5>
                                <h3 className="mb-4 text-primary fw-bold">{selectedJob?.process?.name}</h3>
                                <div className="row justify-content-center mb-4">
                                    <div className="col-6">
                                        <label className="fw-bold text-muted small">QTY DONE</label>
                                        <input type="number" className="form-control form-control-lg text-center fw-bold fs-1 border-success" value={returnQty} onChange={e => setReturnQty(e.target.value)} autoFocus />
                                    </div>
                                </div>
                                <div className="alert alert-warning py-2 fs-5">Payable Wage: <strong>₹ {formatMoney(returnQty * (selectedJob?.process?.rate || 0))}</strong></div>
                            </div>
                            <div className="modal-footer p-2">
                                <button className="btn btn-secondary btn-lg" onClick={() => setShowRecModal(false)}>CANCEL</button>
                                <button className="btn btn-success btn-lg px-5 fw-bold" onClick={confirmReturn}>CONFIRM & PAY</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- REPORT COMPONENT ---
const LaborReport = () => {
    const [logs, setLogs] = useState([]);
    const [view, setView] = useState('list'); // 'list', 'employee', 'dept'
    const [selectedEmpName, setSelectedEmpName] = useState(null); // For drill-down

    // Filters
    const [filterType, setFilterType] = useState('month'); // 'all', 'month', 'date'
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD
    const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

    useEffect(() => {
        api.get('/labor/report').then(res => setLogs(res.data));
    }, []);

    // 1. FILTER LOGIC
    const filteredLogs = useMemo(() => {
        return logs.filter(l => {
            if (!l.returnDate) return false;
            const dateStr = l.returnDate.split('T')[0];
            if (filterType === 'date') return dateStr === filterDate;
            if (filterType === 'month') return dateStr.startsWith(filterMonth);
            return true;
        });
    }, [logs, filterType, filterDate, filterMonth]);

    // 2. AGGREGATIONS
    const totalWage = filteredLogs.reduce((acc, l) => acc + (l.finalAmount || 0), 0);
    const totalPcs = filteredLogs.reduce((acc, l) => acc + (l.quantity || 0), 0);

    const empStats = useMemo(() => {
        const stats = {};
        filteredLogs.forEach(l => {
            const name = l.employee?.name || 'Unknown';
            if(!stats[name]) stats[name] = { count: 0, wage: 0, dept: l.employee?.department?.name };
            stats[name].count += (l.quantity || 0);
            stats[name].wage += (l.finalAmount || 0);
        });
        return stats;
    }, [filteredLogs]);

    const deptStats = useMemo(() => {
        const stats = {};
        filteredLogs.forEach(l => {
            const dName = l.employee?.department?.name || 'No Dept';
            if(!stats[dName]) stats[dName] = { count: 0, wage: 0 };
            stats[dName].count += (l.quantity || 0);
            stats[dName].wage += (l.finalAmount || 0);
        });
        return stats;
    }, [filteredLogs]);

    // 3. EMPLOYEE DETAILS (Drill Down)
    const empDetails = useMemo(() => {
        if (!selectedEmpName) return null;
        
        const empLogs = filteredLogs.filter(l => (l.employee?.name || 'Unknown') === selectedEmpName);
        
        // Process Summary for this employee
        const processSummary = {};
        empLogs.forEach(l => {
            const pName = l.process?.name || 'Unknown';
            if(!processSummary[pName]) processSummary[pName] = { count: 0, wage: 0 };
            processSummary[pName].count += (l.quantity || 0);
            processSummary[pName].wage += (l.finalAmount || 0);
        });

        const totalEarning = empLogs.reduce((acc, l) => acc + (l.finalAmount || 0), 0);
        const totalWork = empLogs.reduce((acc, l) => acc + (l.quantity || 0), 0);

        return { empLogs, processSummary, totalEarning, totalWork };
    }, [filteredLogs, selectedEmpName]);


    // --- RENDER ---
    
    // If viewing specific employee details
    if (selectedEmpName && empDetails) {
        return (
            <div className="animate__animated animate__fadeIn">
                <button className="btn btn-secondary mb-3" onClick={() => setSelectedEmpName(null)}>← Back to Summary</button>
                
                <div className="row g-2 mb-3">
                    <div className="col-md-6">
                        <div className="card bg-primary text-white p-3">
                            <h5>{selectedEmpName}</h5>
                            <h3 className="mb-0 fw-bold">₹ {formatMoney(empDetails.totalEarning)}</h3>
                            <small>{empDetails.totalWork} Packets Completed</small>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-light fw-bold small">Summary by Process</div>
                            <table className="table table-sm mb-0">
                                <tbody>
                                    {Object.entries(empDetails.processSummary).map(([proc, stats]) => (
                                        <tr key={proc}>
                                            <td>{proc}</td>
                                            <td className="text-center">{stats.count} pkts</td>
                                            <td className="text-end fw-bold">₹ {formatMoney(stats.wage)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm mt-3">
                    <div className="card-header bg-dark text-white fw-bold">Detailed Daily Transactions</div>
                    <div className="table-responsive" style={{maxHeight:'400px'}}>
                        <table className="table table-striped mb-0 table-sm">
                            <thead className="table-light"><tr><th>Date</th><th>Kapan No</th><th>Process</th><th className="text-center">Qty</th><th className="text-end">Wage</th></tr></thead>
                            <tbody>
                                {empDetails.empLogs.map(l => (
                                    <tr key={l._id}>
                                        <td>{formatDate(l.returnDate)}</td>
                                        <td>{l.packetNo}</td>
                                        <td>{l.process?.name}</td>
                                        <td className="text-center">{l.quantity}</td>
                                        <td className="text-end fw-bold text-success">₹ {formatMoney(l.finalAmount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // Default Report View
    return (
        <div>
            {/* FILTER CONTROLS */}
            <div className="row g-2 mb-3 align-items-center bg-light p-2 rounded border">
                <div className="col-auto fw-bold text-muted">Period:</div>
                <div className="col-auto">
                    <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                        <option value="all">All Time</option>
                        <option value="month">Monthly</option>
                        <option value="date">Daily</option>
                    </select>
                </div>
                <div className="col-auto">
                    {filterType === 'date' && <input type="date" className="form-control form-control-sm" value={filterDate} onChange={e => setFilterDate(e.target.value)} />}
                    {filterType === 'month' && <input type="month" className="form-control form-control-sm" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} />}
                </div>
            </div>

            {/* SUMMARY CARDS */}
            <div className="row mb-3 g-2">
                <div className="col-4"><div className="card bg-primary text-white text-center p-2"><h4>{totalPcs} Pcs</h4></div></div>
                <div className="col-4"><div className="card bg-success text-white text-center p-2"><h4>₹ {formatMoney(totalWage)}</h4></div></div>
                <div className="col-4"><div className="card bg-light border text-center p-2"><h4 className="text-muted">{filteredLogs.length} Logs</h4></div></div>
            </div>

            {/* VIEW TABS */}
            <div className="btn-group w-100 mb-3">
                <button className={`btn ${view === 'list' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setView('list')}>📜 Logs</button>
                <button className={`btn ${view === 'employee' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setView('employee')}>👤 Employee</button>
                <button className={`btn ${view === 'dept' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setView('dept')}>🏢 Dept</button>
            </div>
            
            <div className="card shadow-sm table-responsive" style={{maxHeight:'400px'}}>
                {/* 1. LOGS VIEW */}
                {view === 'list' && (
                    <table className="table table-striped mb-0">
                        <thead><tr><th>Date</th><th>Worker</th><th>Kapan</th><th>Process</th><th>Qty</th><th className="text-end">Wage</th></tr></thead>
                        <tbody>
                            {filteredLogs.map(l => (
                                <tr key={l._id}>
                                    <td>{formatDate(l.returnDate)}</td>
                                    <td>{l.employee?.name}</td>
                                    <td>{l.packetNo}</td>
                                    <td>{l.process?.name}</td>
                                    <td>{l.quantity}</td>
                                    <td className="text-end fw-bold text-success">₹ {formatMoney(l.finalAmount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* 2. EMPLOYEE VIEW (With Drill Down) */}
                {view === 'employee' && (
                    <table className="table table-hover mb-0">
                        <thead className="table-dark"><tr><th>Name</th><th>Dept</th><th>Pcs</th><th className="text-end">Wage</th></tr></thead>
                        <tbody>
                            {Object.entries(empStats).map(([name, data]) => (
                                <tr key={name} style={{cursor:'pointer'}} onClick={() => setSelectedEmpName(name)}>
                                    <td className="fw-bold text-primary">{name} <span className="small text-muted">↗</span></td>
                                    <td>{data.dept}</td>
                                    <td>{data.count}</td>
                                    <td className="text-end fw-bold text-success">₹ {formatMoney(data.wage)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* 3. DEPT VIEW */}
                {view === 'dept' && (
                    <table className="table table-hover mb-0">
                        <thead className="table-primary"><tr><th>Department</th><th>Pcs</th><th className="text-end">Cost</th></tr></thead>
                        <tbody>
                            {Object.entries(deptStats).map(([name, data]) => (
                                <tr key={name}>
                                    <td className="fw-bold">{name}</td>
                                    <td>{data.count}</td>
                                    <td className="text-end fw-bold text-danger">₹ {formatMoney(data.wage)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default LaborManager;